import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { canAccessPlatform } from "../utils/permissions";
import {
  ShieldCheck,
  MagnifyingGlass,
  Funnel,
  ArrowClockwise,
  Warning,
  Info,
  XCircle,
  SpinnerGap,
  CaretDown,
  User,
  Clock,
  Globe,
  Tag
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

const SEVERITY_CONFIG = {
  info: { color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20", icon: Info, label: "Info" },
  warning: { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20", icon: Warning, label: "Attention" },
  critical: { color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20", icon: XCircle, label: "Critique" },
};

export default function AuditLogPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [limit, setLimit] = useState(100);
  const [expandedLog, setExpandedLog] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.append("action", actionFilter);
      if (severityFilter) params.append("severity", severityFilter);
      params.append("limit", limit);
      const { data } = await axios.get(`${API}/api/platform/audit-logs?${params}`, { withCredentials: true });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, severityFilter, limit]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  if (!canAccessPlatform(user) && user?.role !== "admin") {
    return <div className="p-8 text-center text-muted-foreground">Acces refuse</div>;
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/20 flex items-center justify-center">
            <ShieldCheck size={22} className="text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] tracking-tight" data-testid="audit-log-title">{t("audit_title")}</h1>
            <p className="text-xs text-muted-foreground">{total} {t("events_recorded")}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadLogs} className="gap-2" data-testid="refresh-logs-btn">
          <ArrowClockwise size={14} />
          Rafraichir
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-card border border-border rounded-xl" data-testid="audit-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("filter_by_action")}
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
            data-testid="audit-action-filter"
          />
        </div>
        <div className="relative">
          <Funnel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="pl-8 pr-8 py-2 bg-background border border-border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
            data-testid="audit-severity-filter"
          >
            <option value="">{t("all_severities")}</option>
            <option value="info">{t("severity_info")}</option>
            <option value="warning">{t("severity_warning")}</option>
            <option value="critical">{t("severity_critical")}</option>
          </select>
          <CaretDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="pl-3 pr-8 py-2 bg-background border border-border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
            data-testid="audit-limit-filter"
          >
            <option value={50}>{`50 ${t("lines")}`}</option>
            <option value={100}>{`100 ${t("lines")}`}</option>
            <option value={200}>{`200 ${t("lines")}`}</option>
            <option value={500}>{`500 ${t("lines")}`}</option>
          </select>
          <CaretDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <SpinnerGap size={28} className="animate-spin text-cyan-400" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ShieldCheck size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">{t("no_logs")}</p>
          <p className="text-sm mt-1">{t("modify_filters")}</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="audit-logs-table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-3 text-left font-medium">{t("severity")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("action")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("user_label")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("entity")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("ip")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log, i) => {
                  const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                  const SevIcon = sev.icon;
                  const isExpanded = expandedLog === i;
                  return (
                    <tr
                      key={i}
                      className="hover:bg-secondary/30 transition-colors cursor-pointer text-sm"
                      onClick={() => setExpandedLog(isExpanded ? null : i)}
                      data-testid={`audit-log-row-${i}`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sev.bg}`}>
                          <SevIcon size={12} weight="fill" className={sev.color} />
                          {sev.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono bg-secondary/50 px-1.5 py-0.5 rounded">{log.action}</code>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <User size={13} className="text-muted-foreground" />
                          <span className="text-xs truncate max-w-[180px]">{log.actor_email || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Tag size={13} className="text-muted-foreground" />
                          <span className="text-xs">{log.entity_type || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Globe size={13} className="text-muted-foreground" />
                          <span className="text-xs font-mono">{log.ip_address || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Clock size={13} className="text-muted-foreground" />
                          <span className="text-xs">
                            {log.created_at ? new Date(log.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
            <span>{logs.length} sur {total} evenements</span>
            {logs.length < total && (
              <Button variant="ghost" size="sm" onClick={() => setLimit(l => Math.min(l + 100, 500))} className="text-xs" data-testid="load-more-logs-btn">
                Charger plus
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
